import { useState, useEffect, useRef } from 'react';
import {
  IconArrowLeft,
  IconChevronDown,
} from '@tabler/icons-react';
import { useNavigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import PageHeader from '../components/PageHeader';
import MainCard from '../components/MainCard';
import api from '../lib/api';

export type VerificationFormMode = 'complete' | 'view';

export interface VerificationFormLocationState {
  distributor_i_customer: number;
  sub_distributor_id: number;
  entity_name?: string;
  mode: VerificationFormMode;
}

const defaultValues = {
  companyName: '',
  companyNumber: '',
  taxNumber: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  province: '',
  postalCode: '',
  country: 'ZA',
  firstName: '',
  lastName: '',
  emailAddress: '',
  phoneFullDigits: '',
  phoneContainsInvalidChars: false,
};

const COMPANY_NUMBER_MAX_LENGTH = 14;
const VAT_MAX_LENGTH = 10;
const POSTAL_CODE_MAX_LENGTH = 4;
const PHONE_NATIONAL_MAX_LENGTH = 15;
const PHONE_FULL_INPUT_MAX_LENGTH = 25;

function formatPhoneWithDashes(digits: string): string {
  const d = digits.replace(/\D/g, '');
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}-${d.slice(3)}`;
  if (d.length <= 10) return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
  const rest = d.slice(10);
  return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6, 10)}-${rest}`;
}

function isSequentialDigits(s: string): boolean {
  if (s.length < 2 || !/^\d+$/.test(s)) return false;
  let asc = true;
  let desc = true;
  for (let i = 0; i < s.length - 1; i++) {
    const a = parseInt(s[i], 10);
    const b = parseInt(s[i + 1], 10);
    if ((a + 1) % 10 !== b) asc = false;
    if ((a - 1 + 10) % 10 !== b) desc = false;
    if (!asc && !desc) return false;
  }
  return asc || desc;
}

const COUNTRY_PHONE_OPTIONS: { code: string; dial: string; dialDigits: string; flag: string; label: string }[] = [
  { code: 'ZA', dial: '+27', dialDigits: '27', flag: '🇿🇦', label: 'South Africa' },
  { code: 'AF', dial: '+93', dialDigits: '93', flag: '🇦🇫', label: 'Afghanistan' },
  { code: 'AL', dial: '+355', dialDigits: '355', flag: '🇦🇱', label: 'Albania' },
  { code: 'DZ', dial: '+213', dialDigits: '213', flag: '🇩🇿', label: 'Algeria' },
  { code: 'AS', dial: '+1684', dialDigits: '1684', flag: '🇦🇸', label: 'American Samoa' },
  { code: 'AD', dial: '+376', dialDigits: '376', flag: '🇦🇩', label: 'Andorra' },
  { code: 'AO', dial: '+244', dialDigits: '244', flag: '🇦🇴', label: 'Angola' },
  { code: 'AI', dial: '+1264', dialDigits: '1264', flag: '🇦🇮', label: 'Anguilla' },
  { code: 'AG', dial: '+1268', dialDigits: '1268', flag: '🇦🇬', label: 'Antigua and Barbuda' },
  { code: 'AR', dial: '+54', dialDigits: '54', flag: '🇦🇷', label: 'Argentina' },
  { code: 'AM', dial: '+374', dialDigits: '374', flag: '🇦🇲', label: 'Armenia' },
  { code: 'AW', dial: '+297', dialDigits: '297', flag: '🇦🇼', label: 'Aruba' },
  { code: 'AU', dial: '+61', dialDigits: '61', flag: '🇦🇺', label: 'Australia' },
  { code: 'AT', dial: '+43', dialDigits: '43', flag: '🇦🇹', label: 'Austria' },
  { code: 'AZ', dial: '+994', dialDigits: '994', flag: '🇦🇿', label: 'Azerbaijan' },
  { code: 'BS', dial: '+1242', dialDigits: '1242', flag: '🇧🇸', label: 'Bahamas' },
  { code: 'BH', dial: '+973', dialDigits: '973', flag: '🇧🇭', label: 'Bahrain' },
  { code: 'BD', dial: '+880', dialDigits: '880', flag: '🇧🇩', label: 'Bangladesh' },
  { code: 'BB', dial: '+1246', dialDigits: '1246', flag: '🇧🇧', label: 'Barbados' },
  { code: 'BY', dial: '+375', dialDigits: '375', flag: '🇧🇾', label: 'Belarus' },
  { code: 'BE', dial: '+32', dialDigits: '32', flag: '🇧🇪', label: 'Belgium' },
  { code: 'BZ', dial: '+501', dialDigits: '501', flag: '🇧🇿', label: 'Belize' },
  { code: 'BJ', dial: '+229', dialDigits: '229', flag: '🇧🇯', label: 'Benin' },
  { code: 'BM', dial: '+1441', dialDigits: '1441', flag: '🇧🇲', label: 'Bermuda' },
  { code: 'BT', dial: '+975', dialDigits: '975', flag: '🇧🇹', label: 'Bhutan' },
  { code: 'BO', dial: '+591', dialDigits: '591', flag: '🇧🇴', label: 'Bolivia' },
  { code: 'BA', dial: '+387', dialDigits: '387', flag: '🇧🇦', label: 'Bosnia and Herzegovina' },
  { code: 'BW', dial: '+267', dialDigits: '267', flag: '🇧🇼', label: 'Botswana' },
  { code: 'BR', dial: '+55', dialDigits: '55', flag: '🇧🇷', label: 'Brazil' },
  { code: 'BN', dial: '+673', dialDigits: '673', flag: '🇧🇳', label: 'Brunei' },
  { code: 'BG', dial: '+359', dialDigits: '359', flag: '🇧🇬', label: 'Bulgaria' },
  { code: 'BF', dial: '+226', dialDigits: '226', flag: '🇧🇫', label: 'Burkina Faso' },
  { code: 'BI', dial: '+257', dialDigits: '257', flag: '🇧🇮', label: 'Burundi' },
  { code: 'KH', dial: '+855', dialDigits: '855', flag: '🇰🇭', label: 'Cambodia' },
  { code: 'CM', dial: '+237', dialDigits: '237', flag: '🇨🇲', label: 'Cameroon' },
  { code: 'CA', dial: '+1', dialDigits: '1', flag: '🇨🇦', label: 'Canada' },
  { code: 'CV', dial: '+238', dialDigits: '238', flag: '🇨🇻', label: 'Cape Verde' },
  { code: 'KY', dial: '+1345', dialDigits: '1345', flag: '🇰🇾', label: 'Cayman Islands' },
  { code: 'CF', dial: '+236', dialDigits: '236', flag: '🇨🇫', label: 'Central African Republic' },
  { code: 'TD', dial: '+235', dialDigits: '235', flag: '🇹🇩', label: 'Chad' },
  { code: 'CL', dial: '+56', dialDigits: '56', flag: '🇨🇱', label: 'Chile' },
  { code: 'CN', dial: '+86', dialDigits: '86', flag: '🇨🇳', label: 'China' },
  { code: 'CO', dial: '+57', dialDigits: '57', flag: '🇨🇴', label: 'Colombia' },
  { code: 'KM', dial: '+269', dialDigits: '269', flag: '🇰🇲', label: 'Comoros' },
  { code: 'CG', dial: '+242', dialDigits: '242', flag: '🇨🇬', label: 'Congo' },
  { code: 'CD', dial: '+243', dialDigits: '243', flag: '🇨🇩', label: 'Congo (DRC)' },
  { code: 'CR', dial: '+506', dialDigits: '506', flag: '🇨🇷', label: 'Costa Rica' },
  { code: 'CI', dial: '+225', dialDigits: '225', flag: '🇨🇮', label: "Côte d'Ivoire" },
  { code: 'HR', dial: '+385', dialDigits: '385', flag: '🇭🇷', label: 'Croatia' },
  { code: 'CU', dial: '+53', dialDigits: '53', flag: '🇨🇺', label: 'Cuba' },
  { code: 'CY', dial: '+357', dialDigits: '357', flag: '🇨🇾', label: 'Cyprus' },
  { code: 'CZ', dial: '+420', dialDigits: '420', flag: '🇨🇿', label: 'Czech Republic' },
  { code: 'DK', dial: '+45', dialDigits: '45', flag: '🇩🇰', label: 'Denmark' },
  { code: 'DJ', dial: '+253', dialDigits: '253', flag: '🇩🇯', label: 'Djibouti' },
  { code: 'DM', dial: '+1767', dialDigits: '1767', flag: '🇩🇲', label: 'Dominica' },
  { code: 'DO', dial: '+1809', dialDigits: '1809', flag: '🇩🇴', label: 'Dominican Republic' },
  { code: 'EC', dial: '+593', dialDigits: '593', flag: '🇪🇨', label: 'Ecuador' },
  { code: 'EG', dial: '+20', dialDigits: '20', flag: '🇪🇬', label: 'Egypt' },
  { code: 'SV', dial: '+503', dialDigits: '503', flag: '🇸🇻', label: 'El Salvador' },
  { code: 'GQ', dial: '+240', dialDigits: '240', flag: '🇬🇶', label: 'Equatorial Guinea' },
  { code: 'ER', dial: '+291', dialDigits: '291', flag: '🇪🇷', label: 'Eritrea' },
  { code: 'EE', dial: '+372', dialDigits: '372', flag: '🇪🇪', label: 'Estonia' },
  { code: 'SZ', dial: '+268', dialDigits: '268', flag: '🇸🇿', label: 'Eswatini' },
  { code: 'ET', dial: '+251', dialDigits: '251', flag: '🇪🇹', label: 'Ethiopia' },
  { code: 'FJ', dial: '+679', dialDigits: '679', flag: '🇫🇯', label: 'Fiji' },
  { code: 'FI', dial: '+358', dialDigits: '358', flag: '🇫🇮', label: 'Finland' },
  { code: 'FR', dial: '+33', dialDigits: '33', flag: '🇫🇷', label: 'France' },
  { code: 'GA', dial: '+241', dialDigits: '241', flag: '🇬🇦', label: 'Gabon' },
  { code: 'GM', dial: '+220', dialDigits: '220', flag: '🇬🇲', label: 'Gambia' },
  { code: 'GE', dial: '+995', dialDigits: '995', flag: '🇬🇪', label: 'Georgia' },
  { code: 'DE', dial: '+49', dialDigits: '49', flag: '🇩🇪', label: 'Germany' },
  { code: 'GH', dial: '+233', dialDigits: '233', flag: '🇬🇭', label: 'Ghana' },
  { code: 'GR', dial: '+30', dialDigits: '30', flag: '🇬🇷', label: 'Greece' },
  { code: 'GD', dial: '+1473', dialDigits: '1473', flag: '🇬🇩', label: 'Grenada' },
  { code: 'GT', dial: '+502', dialDigits: '502', flag: '🇬🇹', label: 'Guatemala' },
  { code: 'GN', dial: '+224', dialDigits: '224', flag: '🇬🇳', label: 'Guinea' },
  { code: 'GW', dial: '+245', dialDigits: '245', flag: '🇬🇼', label: 'Guinea-Bissau' },
  { code: 'GY', dial: '+592', dialDigits: '592', flag: '🇬🇾', label: 'Guyana' },
  { code: 'HT', dial: '+509', dialDigits: '509', flag: '🇭🇹', label: 'Haiti' },
  { code: 'HN', dial: '+504', dialDigits: '504', flag: '🇭🇳', label: 'Honduras' },
  { code: 'HK', dial: '+852', dialDigits: '852', flag: '🇭🇰', label: 'Hong Kong' },
  { code: 'HU', dial: '+36', dialDigits: '36', flag: '🇭🇺', label: 'Hungary' },
  { code: 'IS', dial: '+354', dialDigits: '354', flag: '🇮🇸', label: 'Iceland' },
  { code: 'IN', dial: '+91', dialDigits: '91', flag: '🇮🇳', label: 'India' },
  { code: 'ID', dial: '+62', dialDigits: '62', flag: '🇮🇩', label: 'Indonesia' },
  { code: 'IR', dial: '+98', dialDigits: '98', flag: '🇮🇷', label: 'Iran' },
  { code: 'IQ', dial: '+964', dialDigits: '964', flag: '🇮🇶', label: 'Iraq' },
  { code: 'IE', dial: '+353', dialDigits: '353', flag: '🇮🇪', label: 'Ireland' },
  { code: 'IL', dial: '+972', dialDigits: '972', flag: '🇮🇱', label: 'Israel' },
  { code: 'IT', dial: '+39', dialDigits: '39', flag: '🇮🇹', label: 'Italy' },
  { code: 'JM', dial: '+1876', dialDigits: '1876', flag: '🇯🇲', label: 'Jamaica' },
  { code: 'JP', dial: '+81', dialDigits: '81', flag: '🇯🇵', label: 'Japan' },
  { code: 'JO', dial: '+962', dialDigits: '962', flag: '🇯🇴', label: 'Jordan' },
  { code: 'KZ', dial: '+7', dialDigits: '7', flag: '🇰🇿', label: 'Kazakhstan' },
  { code: 'KE', dial: '+254', dialDigits: '254', flag: '🇰🇪', label: 'Kenya' },
  { code: 'KI', dial: '+686', dialDigits: '686', flag: '🇰🇮', label: 'Kiribati' },
  { code: 'KW', dial: '+965', dialDigits: '965', flag: '🇰🇼', label: 'Kuwait' },
  { code: 'KG', dial: '+996', dialDigits: '996', flag: '🇰🇬', label: 'Kyrgyzstan' },
  { code: 'LA', dial: '+856', dialDigits: '856', flag: '🇱🇦', label: 'Laos' },
  { code: 'LV', dial: '+371', dialDigits: '371', flag: '🇱🇻', label: 'Latvia' },
  { code: 'LB', dial: '+961', dialDigits: '961', flag: '🇱🇧', label: 'Lebanon' },
  { code: 'LS', dial: '+266', dialDigits: '266', flag: '🇱🇸', label: 'Lesotho' },
  { code: 'LR', dial: '+231', dialDigits: '231', flag: '🇱🇷', label: 'Liberia' },
  { code: 'LY', dial: '+218', dialDigits: '218', flag: '🇱🇾', label: 'Libya' },
  { code: 'LI', dial: '+423', dialDigits: '423', flag: '🇱🇮', label: 'Liechtenstein' },
  { code: 'LT', dial: '+370', dialDigits: '370', flag: '🇱🇹', label: 'Lithuania' },
  { code: 'LU', dial: '+352', dialDigits: '352', flag: '🇱🇺', label: 'Luxembourg' },
  { code: 'MO', dial: '+853', dialDigits: '853', flag: '🇲🇴', label: 'Macau' },
  { code: 'MG', dial: '+261', dialDigits: '261', flag: '🇲🇬', label: 'Madagascar' },
  { code: 'MW', dial: '+265', dialDigits: '265', flag: '🇲🇼', label: 'Malawi' },
  { code: 'MY', dial: '+60', dialDigits: '60', flag: '🇲🇾', label: 'Malaysia' },
  { code: 'MV', dial: '+960', dialDigits: '960', flag: '🇲🇻', label: 'Maldives' },
  { code: 'ML', dial: '+223', dialDigits: '223', flag: '🇲🇱', label: 'Mali' },
  { code: 'MT', dial: '+356', dialDigits: '356', flag: '🇲🇹', label: 'Malta' },
  { code: 'MH', dial: '+692', dialDigits: '692', flag: '🇲🇭', label: 'Marshall Islands' },
  { code: 'MR', dial: '+222', dialDigits: '222', flag: '🇲🇷', label: 'Mauritania' },
  { code: 'MU', dial: '+230', dialDigits: '230', flag: '🇲🇺', label: 'Mauritius' },
  { code: 'MX', dial: '+52', dialDigits: '52', flag: '🇲🇽', label: 'Mexico' },
  { code: 'FM', dial: '+691', dialDigits: '691', flag: '🇫🇲', label: 'Micronesia' },
  { code: 'MD', dial: '+373', dialDigits: '373', flag: '🇲🇩', label: 'Moldova' },
  { code: 'MC', dial: '+377', dialDigits: '377', flag: '🇲🇨', label: 'Monaco' },
  { code: 'MN', dial: '+976', dialDigits: '976', flag: '🇲🇳', label: 'Mongolia' },
  { code: 'ME', dial: '+382', dialDigits: '382', flag: '🇲🇪', label: 'Montenegro' },
  { code: 'MS', dial: '+1664', dialDigits: '1664', flag: '🇲🇸', label: 'Montserrat' },
  { code: 'MA', dial: '+212', dialDigits: '212', flag: '🇲🇦', label: 'Morocco' },
  { code: 'MZ', dial: '+258', dialDigits: '258', flag: '🇲🇿', label: 'Mozambique' },
  { code: 'MM', dial: '+95', dialDigits: '95', flag: '🇲🇲', label: 'Myanmar' },
  { code: 'NA', dial: '+264', dialDigits: '264', flag: '🇳🇦', label: 'Namibia' },
  { code: 'NR', dial: '+674', dialDigits: '674', flag: '🇳🇷', label: 'Nauru' },
  { code: 'NP', dial: '+977', dialDigits: '977', flag: '🇳🇵', label: 'Nepal' },
  { code: 'NL', dial: '+31', dialDigits: '31', flag: '🇳🇱', label: 'Netherlands' },
  { code: 'NZ', dial: '+64', dialDigits: '64', flag: '🇳🇿', label: 'New Zealand' },
  { code: 'NI', dial: '+505', dialDigits: '505', flag: '🇳🇮', label: 'Nicaragua' },
  { code: 'NE', dial: '+227', dialDigits: '227', flag: '🇳🇪', label: 'Niger' },
  { code: 'NG', dial: '+234', dialDigits: '234', flag: '🇳🇬', label: 'Nigeria' },
  { code: 'MK', dial: '+389', dialDigits: '389', flag: '🇲🇰', label: 'North Macedonia' },
  { code: 'NO', dial: '+47', dialDigits: '47', flag: '🇳🇴', label: 'Norway' },
  { code: 'OM', dial: '+968', dialDigits: '968', flag: '🇴🇲', label: 'Oman' },
  { code: 'PK', dial: '+92', dialDigits: '92', flag: '🇵🇰', label: 'Pakistan' },
  { code: 'PW', dial: '+680', dialDigits: '680', flag: '🇵🇼', label: 'Palau' },
  { code: 'PS', dial: '+970', dialDigits: '970', flag: '🇵🇸', label: 'Palestine' },
  { code: 'PA', dial: '+507', dialDigits: '507', flag: '🇵🇦', label: 'Panama' },
  { code: 'PG', dial: '+675', dialDigits: '675', flag: '🇵🇬', label: 'Papua New Guinea' },
  { code: 'PY', dial: '+595', dialDigits: '595', flag: '🇵🇾', label: 'Paraguay' },
  { code: 'PE', dial: '+51', dialDigits: '51', flag: '🇵🇪', label: 'Peru' },
  { code: 'PH', dial: '+63', dialDigits: '63', flag: '🇵🇭', label: 'Philippines' },
  { code: 'PL', dial: '+48', dialDigits: '48', flag: '🇵🇱', label: 'Poland' },
  { code: 'PT', dial: '+351', dialDigits: '351', flag: '🇵🇹', label: 'Portugal' },
  { code: 'PR', dial: '+1787', dialDigits: '1787', flag: '🇵🇷', label: 'Puerto Rico' },
  { code: 'QA', dial: '+974', dialDigits: '974', flag: '🇶🇦', label: 'Qatar' },
  { code: 'RO', dial: '+40', dialDigits: '40', flag: '🇷🇴', label: 'Romania' },
  { code: 'RU', dial: '+7', dialDigits: '7', flag: '🇷🇺', label: 'Russia' },
  { code: 'RW', dial: '+250', dialDigits: '250', flag: '🇷🇼', label: 'Rwanda' },
  { code: 'KN', dial: '+1869', dialDigits: '1869', flag: '🇰🇳', label: 'Saint Kitts and Nevis' },
  { code: 'LC', dial: '+1758', dialDigits: '1758', flag: '🇱🇨', label: 'Saint Lucia' },
  { code: 'VC', dial: '+1784', dialDigits: '1784', flag: '🇻🇨', label: 'Saint Vincent and the Grenadines' },
  { code: 'WS', dial: '+685', dialDigits: '685', flag: '🇼🇸', label: 'Samoa' },
  { code: 'SM', dial: '+378', dialDigits: '378', flag: '🇸🇲', label: 'San Marino' },
  { code: 'ST', dial: '+239', dialDigits: '239', flag: '🇸🇹', label: 'São Tomé and Príncipe' },
  { code: 'SA', dial: '+966', dialDigits: '966', flag: '🇸🇦', label: 'Saudi Arabia' },
  { code: 'SN', dial: '+221', dialDigits: '221', flag: '🇸🇳', label: 'Senegal' },
  { code: 'RS', dial: '+381', dialDigits: '381', flag: '🇷🇸', label: 'Serbia' },
  { code: 'SC', dial: '+248', dialDigits: '248', flag: '🇸🇨', label: 'Seychelles' },
  { code: 'SL', dial: '+232', dialDigits: '232', flag: '🇸🇱', label: 'Sierra Leone' },
  { code: 'SG', dial: '+65', dialDigits: '65', flag: '🇸🇬', label: 'Singapore' },
  { code: 'SK', dial: '+421', dialDigits: '421', flag: '🇸🇰', label: 'Slovakia' },
  { code: 'SI', dial: '+386', dialDigits: '386', flag: '🇸🇮', label: 'Slovenia' },
  { code: 'SB', dial: '+677', dialDigits: '677', flag: '🇸🇧', label: 'Solomon Islands' },
  { code: 'SO', dial: '+252', dialDigits: '252', flag: '🇸🇴', label: 'Somalia' },
  { code: 'KR', dial: '+82', dialDigits: '82', flag: '🇰🇷', label: 'South Korea' },
  { code: 'SS', dial: '+211', dialDigits: '211', flag: '🇸🇸', label: 'South Sudan' },
  { code: 'ES', dial: '+34', dialDigits: '34', flag: '🇪🇸', label: 'Spain' },
  { code: 'LK', dial: '+94', dialDigits: '94', flag: '🇱🇰', label: 'Sri Lanka' },
  { code: 'SD', dial: '+249', dialDigits: '249', flag: '🇸🇩', label: 'Sudan' },
  { code: 'SR', dial: '+597', dialDigits: '597', flag: '🇸🇷', label: 'Suriname' },
  { code: 'SE', dial: '+46', dialDigits: '46', flag: '🇸🇪', label: 'Sweden' },
  { code: 'CH', dial: '+41', dialDigits: '41', flag: '🇨🇭', label: 'Switzerland' },
  { code: 'SY', dial: '+963', dialDigits: '963', flag: '🇸🇾', label: 'Syria' },
  { code: 'TW', dial: '+886', dialDigits: '886', flag: '🇹🇼', label: 'Taiwan' },
  { code: 'TJ', dial: '+992', dialDigits: '992', flag: '🇹🇯', label: 'Tajikistan' },
  { code: 'TZ', dial: '+255', dialDigits: '255', flag: '🇹🇿', label: 'Tanzania' },
  { code: 'TH', dial: '+66', dialDigits: '66', flag: '🇹🇭', label: 'Thailand' },
  { code: 'TL', dial: '+670', dialDigits: '670', flag: '🇹🇱', label: 'Timor-Leste' },
  { code: 'TG', dial: '+228', dialDigits: '228', flag: '🇹🇬', label: 'Togo' },
  { code: 'TO', dial: '+676', dialDigits: '676', flag: '🇹🇴', label: 'Tonga' },
  { code: 'TT', dial: '+1868', dialDigits: '1868', flag: '🇹🇹', label: 'Trinidad and Tobago' },
  { code: 'TN', dial: '+216', dialDigits: '216', flag: '🇹🇳', label: 'Tunisia' },
  { code: 'TR', dial: '+90', dialDigits: '90', flag: '🇹🇷', label: 'Turkey' },
  { code: 'TM', dial: '+993', dialDigits: '993', flag: '🇹🇲', label: 'Turkmenistan' },
  { code: 'TC', dial: '+1649', dialDigits: '1649', flag: '🇹🇨', label: 'Turks and Caicos' },
  { code: 'TV', dial: '+688', dialDigits: '688', flag: '🇹🇻', label: 'Tuvalu' },
  { code: 'UG', dial: '+256', dialDigits: '256', flag: '🇺🇬', label: 'Uganda' },
  { code: 'UA', dial: '+380', dialDigits: '380', flag: '🇺🇦', label: 'Ukraine' },
  { code: 'AE', dial: '+971', dialDigits: '971', flag: '🇦🇪', label: 'United Arab Emirates' },
  { code: 'GB', dial: '+44', dialDigits: '44', flag: '🇬🇧', label: 'United Kingdom' },
  { code: 'US', dial: '+1', dialDigits: '1', flag: '🇺🇸', label: 'United States' },
  { code: 'UY', dial: '+598', dialDigits: '598', flag: '🇺🇾', label: 'Uruguay' },
  { code: 'UZ', dial: '+998', dialDigits: '998', flag: '🇺🇿', label: 'Uzbekistan' },
  { code: 'VU', dial: '+678', dialDigits: '678', flag: '🇻🇺', label: 'Vanuatu' },
  { code: 'VA', dial: '+379', dialDigits: '379', flag: '🇻🇦', label: 'Vatican City' },
  { code: 'VE', dial: '+58', dialDigits: '58', flag: '🇻🇪', label: 'Venezuela' },
  { code: 'VN', dial: '+84', dialDigits: '84', flag: '🇻🇳', label: 'Vietnam' },
  { code: 'YE', dial: '+967', dialDigits: '967', flag: '🇾🇪', label: 'Yemen' },
  { code: 'ZM', dial: '+260', dialDigits: '260', flag: '🇿🇲', label: 'Zambia' },
  { code: 'ZW', dial: '+263', dialDigits: '263', flag: '🇿🇼', label: 'Zimbabwe' },
];
const COUNTRY_PHONE_OPTIONS_SORTED = [...COUNTRY_PHONE_OPTIONS].sort(
  (a, b) => b.dialDigits.length - a.dialDigits.length,
);

function splitBuffer(buffer: string): { countryDigits: string; nationalDigits: string } {
  const digits = (buffer ?? '').replace(/\D/g, '');
  if (digits.length >= 10 && digits.startsWith('0')) {
    return { countryDigits: '27', nationalDigits: digits.slice(1) };
  }
  for (const opt of COUNTRY_PHONE_OPTIONS_SORTED) {
    if (digits.startsWith(opt.dialDigits)) {
      return {
        countryDigits: opt.dialDigits,
        nationalDigits: digits.slice(opt.dialDigits.length),
      };
    }
  }
  return { countryDigits: digits, nationalDigits: '' };
}

const verificationFormSchema = z.object({
  companyName: z.string().trim().min(1, 'Company Name is required'),
  companyNumber: z
    .string()
    .trim()
    .min(1, 'Company Registration Number is required')
    .regex(/^\d{4}\/\d{6}\/\d{2}$/, 'Use format YYYY/NNNNNN/XX (e.g. 2020/123456/07)'),
  taxNumber: z
    .string()
    .trim()
    .min(1, 'VAT Number is required')
    .regex(/^[0-9]{10}$/, 'Must be exactly 10 digits'),
  addressLine1: z.string().trim().min(1, 'Address Line 1 is required'),
  addressLine2: z.string().trim(),
  city: z.string().trim().min(1, 'City is required'),
  province: z.string().min(1, 'Province is required'),
  postalCode: z
    .string()
    .trim()
    .min(1, 'Postal Code is required')
    .regex(/^\d{4}$/, 'Postal Code must be 4 digits'),
  country: z.string().trim().min(1, 'Country is required'),
  firstName: z.string().trim().min(1, 'First Name is required'),
  lastName: z.string().trim().min(1, 'Last Name is required'),
  emailAddress: z
    .string()
    .trim()
    .min(1, 'Email is required')
    .email('Invalid email'),
  phoneFullDigits: z.string(),
  phoneContainsInvalidChars: z.boolean().optional(),
}).superRefine((data, ctx) => {
  const buffer = (data.phoneFullDigits ?? '').replace(/\D/g, '');
  const { countryDigits, nationalDigits } = splitBuffer(buffer);
  const fullDigits = countryDigits + nationalDigits;
  const isMatchedCode = COUNTRY_PHONE_OPTIONS.some((o) => o.dialDigits === countryDigits);

  if (fullDigits.length === 0) {
    ctx.addIssue({ code: 'custom', path: ['phoneFullDigits'], message: 'Phone number is required.' });
    return;
  }
  if (data.phoneContainsInvalidChars) {
    ctx.addIssue({ code: 'custom', path: ['phoneFullDigits'], message: 'Phone number cannot contain letters or invalid characters (use only digits and separators like spaces or dashes).' });
    return;
  }
  if (!isMatchedCode) {
    ctx.addIssue({ code: 'custom', path: ['phoneFullDigits'], message: 'Select or type a valid country code (e.g. +27 for South Africa).' });
    return;
  }
  if (fullDigits.length > 15) {
    ctx.addIssue({ code: 'custom', path: ['phoneFullDigits'], message: 'Total length cannot exceed 15 digits (E.164).' });
    return;
  }
  const normalizedNational =
    countryDigits !== '1' && nationalDigits.length > 1 && nationalDigits[0] === '0'
      ? nationalDigits.slice(1)
      : nationalDigits;
  const NATIONAL_MIN = 3;
  const NATIONAL_MAX = 14;
  if (normalizedNational.length < NATIONAL_MIN || normalizedNational.length > NATIONAL_MAX) {
    if (countryDigits === '27' && normalizedNational.length > 0 && normalizedNational.length < 9) {
      ctx.addIssue({ code: 'custom', path: ['phoneFullDigits'], message: `South African numbers need 9 digits after +27 (e.g. 64 082 2549). You have ${normalizedNational.length} digit(s).` });
    } else {
      ctx.addIssue({ code: 'custom', path: ['phoneFullDigits'], message: `Number after country code must be ${NATIONAL_MIN}–${NATIONAL_MAX} digits. You have ${normalizedNational.length}.` });
    }
    return;
  }
  if (!/^[1-9]\d{3,14}$/.test(fullDigits)) {
    ctx.addIssue({ code: 'custom', path: ['phoneFullDigits'], message: 'Enter a valid phone number (no leading zero after country code; e.g. +27 64 082 2549).' });
    return;
  }
  if (normalizedNational.length >= 2 && /^(\d)\1*$/.test(normalizedNational)) {
    ctx.addIssue({ code: 'custom', path: ['phoneFullDigits'], message: 'Numbers cannot be a single repeating digit (e.g. 2222222222).' });
    return;
  }
  if (normalizedNational.length >= 2 && isSequentialDigits(normalizedNational)) {
    ctx.addIssue({ code: 'custom', path: ['phoneFullDigits'], message: 'Sequential numbers (e.g. 1234567890) are not allowed.' });
    return;
  }
  if (countryDigits === '1' && nationalDigits.length > 0 && nationalDigits[0] === '0') {
    ctx.addIssue({ code: 'custom', path: ['phoneFullDigits'], message: 'US/Canada numbers cannot start with 0 after the country code.' });
    return;
  }
  if (countryDigits === '1' && nationalDigits.length > 0 && nationalDigits[0] === '1') {
    ctx.addIssue({ code: 'custom', path: ['phoneFullDigits'], message: 'US/Canada numbers cannot start with 1 after the country code.' });
    return;
  }
  if (countryDigits === '1' && normalizedNational.startsWith('555')) {
    ctx.addIssue({ code: 'custom', path: ['phoneFullDigits'], message: '555 numbers are reserved (e.g. directory assistance) and are not valid for contact numbers.' });
  }
});

export type VerificationFormValues = z.infer<typeof verificationFormSchema>;

export function SubDistributorVerificationFormPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ distributorICustomer?: string; subDistributorId?: string }>();
  const [searchParams] = useSearchParams();
  const state = location.state as VerificationFormLocationState | null;

  const distributorICustomer = (() => {
    const fromParams = params.distributorICustomer != null ? Number(params.distributorICustomer) : NaN;
    if (Number.isFinite(fromParams)) return fromParams;
    return state?.distributor_i_customer;
  })();
  const subDistributorId = (() => {
    const fromParams = params.subDistributorId != null ? Number(params.subDistributorId) : NaN;
    if (Number.isFinite(fromParams)) return fromParams;
    return state?.sub_distributor_id;
  })();
  const modeFromUrl = searchParams.get('mode');
  const mode: VerificationFormMode =
    state?.mode ?? (modeFromUrl === 'complete' || modeFromUrl === 'view' ? modeFromUrl : 'view');
  const entityName = state?.entity_name;

  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsLoaded, setDetailsLoaded] = useState(false);
  const [provinces, setProvinces] = useState<{ id: number; name: string }[]>([]);
  const [detailsMeta, setDetailsMeta] = useState<{
    xero_contact_id: string | null;
    details_verified: boolean;
    province_id?: number | null;
  } | null>(null);
  const provincesRef = useRef<{ id: number; name: string }[]>([]);
  provincesRef.current = provinces;

  const {
    register,
    control,
    handleSubmit: rhfHandleSubmit,
    formState: { errors, isValid },
    reset,
    trigger,
    setValue,
    getValues,
    watch,
  } = useForm<VerificationFormValues>({
    defaultValues,
    resolver: zodResolver(verificationFormSchema),
    mode: 'onBlur',
  });

  const isViewMode = mode === 'view';
  const isCompleteMode = mode === 'complete';

  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const fieldHasContent = (field: keyof VerificationFormValues): boolean => {
    const v = getValues(field);
    if (field === 'phoneFullDigits') return ((v ?? '') + '').replace(/\D/g, '').length > 0;
    if (typeof v === 'string') return v.trim() !== '';
    return false;
  };
  const showError = (field: keyof VerificationFormValues) =>
    !!errors[field] &&
    (touched[field] || submitAttempted || (fieldHasContent(field) && isCompleteMode && hasValidatedOnce && detailsLoaded));

  const registerWithTouched = (name: keyof VerificationFormValues) => {
    const r = register(name);
    return {
      ...r,
      onBlur: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        r.onBlur(e);
        setTouched((prev) => ({ ...prev, [name]: true }));
      },
    };
  };

  useEffect(() => {
    let cancelled = false;
    api.get<{ success: boolean; data: { id: number; name: string }[] }>('/distributors/provinces').then((res) => {
      if (!cancelled && Array.isArray(res.data?.data)) setProvinces(res.data.data);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const [hasValidatedOnce, setHasValidatedOnce] = useState(false);
  useEffect(() => {
    if (isCompleteMode) {
      trigger().then(() => setHasValidatedOnce(true));
    }
  }, [isCompleteMode, trigger]);

  useEffect(() => {
    if (distributorICustomer == null || subDistributorId == null) return;
    let cancelled = false;
    setDetailsLoading(true);
    api
      .get<{ success: boolean; data: Record<string, unknown> | null }>(
        '/distributors/verification-status/details',
        {
          params: {
            distributor_i_customer: distributorICustomer,
            sub_distributor_id: subDistributorId,
          },
        },
      )
      .then((res) => {
        if (cancelled) return;
        const data = res.data?.data as {
          xero_contact_id?: string | null;
          details_verified?: boolean;
          company_name?: string | null;
          company_registration_number?: string | null;
          company_vat_number?: string | null;
          address_line_1?: string | null;
          address_line_2?: string | null;
          city?: string | null;
          province_id?: number | null;
          province_name?: string | null;
          postal_code?: string | null;
          country?: string | null;
          contact_first_name?: string | null;
          contact_last_name?: string | null;
          contact_email_address?: string | null;
          contact_phone_number?: string | null;
        } | null;
        if (data) {
          const provinceId = data.province_id != null ? Number(data.province_id) : null;
          setDetailsMeta({
            xero_contact_id: data.xero_contact_id ?? null,
            details_verified: data.details_verified === true,
            province_id: provinceId ?? undefined,
          });
          const currentProvinces = provincesRef.current;
          const provinceName =
            (data.province_name && String(data.province_name).trim()) ||
            (provinceId != null && currentProvinces.length > 0 ? currentProvinces.find((p) => p.id === provinceId)?.name ?? '' : '') ||
            '';
          const digits = (data.contact_phone_number ?? '').replace(/\D/g, '');
          const { countryDigits, nationalDigits } = splitBuffer(digits);
          const phoneFullDigits = countryDigits + nationalDigits;
          reset({
            companyName: data.company_name ?? '',
            companyNumber: data.company_registration_number ?? '',
            taxNumber: data.company_vat_number ?? '',
            addressLine1: data.address_line_1 ?? '',
            addressLine2: data.address_line_2 ?? '',
            city: data.city ?? '',
            province: provinceName,
            postalCode: data.postal_code ?? '',
            country: (data.country && String(data.country).trim()) || 'ZA',
            firstName: data.contact_first_name ?? '',
            lastName: data.contact_last_name ?? '',
            emailAddress: data.contact_email_address ?? '',
            phoneFullDigits,
            phoneContainsInvalidChars: false,
          });
          trigger().then(() => setHasValidatedOnce(true));
          setTouched({});
        }
        setDetailsLoaded(true);
      })
      .catch(() => {
        if (!cancelled) setDetailsLoaded(true);
      })
      .finally(() => {
        if (!cancelled) setDetailsLoading(false);
      });
    return () => { cancelled = true; };
  }, [distributorICustomer, subDistributorId, reset, trigger]);

  useEffect(() => {
    if (provinces.length === 0 || detailsMeta?.province_id == null) return;
    if (getValues('province') !== '') return;
    const name = provinces.find((p) => p.id === detailsMeta!.province_id)?.name ?? '';
    if (name) setValue('province', name);
  }, [provinces, detailsMeta, setValue, getValues]);

  useEffect(() => {
    if (distributorICustomer == null || subDistributorId == null) {
      navigate('/spar-mobile/store-compliance', { replace: true });
    }
  }, [distributorICustomer, subDistributorId, navigate]);

  if (distributorICustomer == null || subDistributorId == null) {
    return null;
  }

  const buildPayload = (values: VerificationFormValues) => {
    const buffer = (values.phoneFullDigits ?? '').replace(/\D/g, '').slice(0, 15);
    const { countryDigits, nationalDigits } = splitBuffer(buffer);
    const normalizedNational =
      countryDigits !== '1' && nationalDigits.startsWith('0') ? nationalDigits.slice(1) : nationalDigits;
    const fullDigits = countryDigits + normalizedNational;
    const phoneE164 = fullDigits.length > 0 ? '+' + fullDigits : '';
    const provinceId = values.province ? provinces.find((p) => p.name === values.province)?.id ?? null : null;
    return {
      accountNumber: String(subDistributorId),
      companyName: values.companyName.trim(),
      companyNumber: values.companyNumber.trim(),
      taxNumber: values.taxNumber.trim(),
      addresses: [
        {
          addressLine1: values.addressLine1.trim(),
          addressLine2: values.addressLine2?.trim() || undefined,
          city: values.city.trim(),
          region: values.province,
          Region: values.province,
          postalCode: values.postalCode.trim(),
        },
      ],
      phones: [{ phoneNumber: phoneE164 }],
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      emailAddress: values.emailAddress.trim(),
      distributor_i_customer: distributorICustomer,
      sub_distributor_id: subDistributorId,
      province_id: provinceId,
    };
  };

  const onSubmit = async (values: VerificationFormValues) => {
    setSubmitError(null);
    setSubmitting(true);
    try {
      const payload = buildPayload(values);
      const res = await api.post<{ status_code: number; data?: unknown }>('/distributors/verification-status/verify', payload);
      if (res.data?.status_code === 1001) {
        setSubmitSuccess(true);
        setTimeout(() => navigate('/spar-mobile/store-compliance', { replace: true }), 1500);
      } else {
        const message = typeof res.data?.data === 'string' ? res.data.data : (res.data?.data as { message?: string })?.message ?? 'Verification request failed';
        setSubmitError(message);
      }
    } catch (err: unknown) {
      const data = err && typeof err === 'object' && 'response' in err ? (err as { response?: { data?: { data?: unknown; message?: string } } }).response?.data : undefined;
      const message = (data && (typeof data.data === 'string' ? data.data : (data as { message?: string }).message)) || (err instanceof Error ? err.message : 'Something went wrong');
      setSubmitError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-[960px]">
      <PageHeader
        title={isViewMode ? 'View verification details' : 'Complete verification'}
        subtitle={
          entityName
            ? `${entityName} (Level 3 sub-distributor)`
            : `Sub-distributor ID: ${subDistributorId}`
        }
        action={
          <Button
            variant="outline"
            onClick={() => navigate('/spar-mobile/store-compliance')}
          >
            <IconArrowLeft size={16} />
            Back to list
          </Button>
        }
      />
      {detailsLoading && (
        <Alert className="mb-4 border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-200">
          <AlertDescription>
            {isViewMode ? 'Loading stored details…' : 'Loading existing details…'}
          </AlertDescription>
        </Alert>
      )}
      {!detailsLoading && isViewMode && (
        <Alert className="mb-4 border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-200">
          <AlertDescription>
            {detailsLoaded ? 'View-only. Stored details are shown below.' : 'View-only. No stored details found for this sub-distributor.'}
          </AlertDescription>
        </Alert>
      )}
      {submitSuccess && (
        <Alert className="mb-4 border-green-200 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-950 dark:text-green-200">
          <AlertDescription>
            Details submitted successfully. Redirecting to list…
          </AlertDescription>
        </Alert>
      )}
      {submitError && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{submitError}</AlertDescription>
        </Alert>
      )}
      <form onSubmit={rhfHandleSubmit(onSubmit)} noValidate>
      <MainCard className="mb-4">
        <h3 className="text-lg font-semibold mb-1">Sub-Distributor Details</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Please provide your details to continue.
        </p>
        <div className="flex gap-4 mb-4 flex-wrap">
          <p className="text-sm text-muted-foreground">
            <strong>Sub-distributor ID:</strong> {subDistributorId}
          </p>
          <p className="text-sm text-muted-foreground">
            <strong>Distributor i_customer:</strong> {distributorICustomer}
          </p>
          {detailsMeta && (
            <>
              <p className="text-sm text-muted-foreground">
                <strong>Details verified:</strong> {detailsMeta.details_verified ? 'Yes' : 'No'}
              </p>
              {detailsMeta.xero_contact_id && (
                <p className="text-sm text-muted-foreground">
                  <strong>Xero contact ID:</strong> {detailsMeta.xero_contact_id}
                </p>
              )}
            </>
          )}
        </div>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="companyName">Company Name <span className="text-destructive">*</span></Label>
            <Input
              id="companyName"
              placeholder="e.g. ABC Trading (Pty) Ltd"
              {...registerWithTouched('companyName')}
              aria-invalid={showError('companyName') || undefined}
              disabled={isViewMode}
            />
            {showError('companyName') && <p className="text-xs text-destructive">{errors.companyName?.message}</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="companyNumber">Company Registration Number <span className="text-destructive">*</span></Label>
            <Controller
              name="companyNumber"
              control={control}
              render={({ field }) => (
                <Input
                  id="companyNumber"
                  placeholder="e.g. 2020/123456/07"
                  maxLength={COMPANY_NUMBER_MAX_LENGTH}
                  {...field}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^\d/]/g, '').slice(0, COMPANY_NUMBER_MAX_LENGTH);
                    field.onChange(value);
                    if (value) trigger('companyNumber');
                  }}
                  onBlur={() => {
                    field.onBlur();
                    setTouched((prev) => ({ ...prev, companyNumber: true }));
                  }}
                  aria-invalid={showError('companyNumber') || undefined}
                  disabled={isViewMode}
                />
              )}
            />
            {showError('companyNumber') && <p className="text-xs text-destructive">{errors.companyNumber?.message}</p>}
            {!isViewMode && !showError('companyNumber') && (
              <p className="text-xs text-muted-foreground max-w-[480px]">
                Format: YYYY/NNNNNN/XX (year, 6-digit number, type code e.g. 07 for (Pty) Ltd)
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="taxNumber">VAT Number <span className="text-destructive">*</span></Label>
            <Controller
              name="taxNumber"
              control={control}
              render={({ field }) => (
                <Input
                  id="taxNumber"
                  placeholder="10 digits"
                  maxLength={VAT_MAX_LENGTH}
                  inputMode="numeric"
                  {...field}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, VAT_MAX_LENGTH);
                    field.onChange(value);
                    if (value) trigger('taxNumber');
                  }}
                  onBlur={() => {
                    field.onBlur();
                    setTouched((prev) => ({ ...prev, taxNumber: true }));
                  }}
                  aria-invalid={showError('taxNumber') || undefined}
                  disabled={isViewMode}
                />
              )}
            />
            {showError('taxNumber') && <p className="text-xs text-destructive">{errors.taxNumber?.message}</p>}
          </div>
        </div>
      </MainCard>

      <MainCard className="mb-4">
        <h3 className="text-lg font-semibold mb-4">Primary Address</h3>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="addressLine1">Address Line 1 <span className="text-destructive">*</span></Label>
            <Input
              id="addressLine1"
              placeholder="e.g. 123 Main Road, Suburb"
              {...registerWithTouched('addressLine1')}
              aria-invalid={showError('addressLine1') || undefined}
              disabled={isViewMode}
            />
            {showError('addressLine1') && <p className="text-xs text-destructive">{errors.addressLine1?.message}</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="addressLine2">Address Line 2</Label>
            <Input
              id="addressLine2"
              placeholder="e.g. Unit 5, Building B (optional)"
              {...registerWithTouched('addressLine2')}
              aria-invalid={showError('addressLine2') || undefined}
              disabled={isViewMode}
            />
            {showError('addressLine2') && <p className="text-xs text-destructive">{errors.addressLine2?.message}</p>}
          </div>
          <div className="flex flex-col sm:flex-row gap-4 items-start">
            <div className="flex-1 min-w-0 space-y-1.5 w-full">
              <Label htmlFor="city">City <span className="text-destructive">*</span></Label>
              <Input
                id="city"
                placeholder="e.g. City or town name"
                {...registerWithTouched('city')}
                aria-invalid={showError('city') || undefined}
                disabled={isViewMode}
              />
              {showError('city') && <p className="text-xs text-destructive">{errors.city?.message}</p>}
            </div>
            <div className="flex-1 min-w-0 space-y-1.5 w-full">
              <Label>Province <span className="text-destructive">*</span></Label>
              <Controller
                name="province"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onValueChange={(val) => {
                      field.onChange(val);
                      setTouched((prev) => ({ ...prev, province: true }));
                    }}
                    disabled={isViewMode}
                  >
                    <SelectTrigger className={cn('w-full', showError('province') && 'border-destructive')}>
                      <SelectValue placeholder="Select province" />
                    </SelectTrigger>
                    <SelectContent>
                      {provinces.map((p) => (
                        <SelectItem key={p.id} value={p.name}>
                          {p.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {showError('province') && <p className="text-xs text-destructive">{errors.province?.message}</p>}
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 items-start">
            <div className="flex-1 min-w-0 space-y-1.5 w-full">
              <Label htmlFor="postalCode">Postal Code <span className="text-destructive">*</span></Label>
              <Controller
                name="postalCode"
                control={control}
                render={({ field }) => (
                  <Input
                    id="postalCode"
                    placeholder="4 digits"
                    maxLength={POSTAL_CODE_MAX_LENGTH}
                    inputMode="numeric"
                    {...field}
                    onChange={(e) => field.onChange(e.target.value.replace(/\D/g, '').slice(0, POSTAL_CODE_MAX_LENGTH))}
                    onBlur={() => {
                      field.onBlur();
                      setTouched((prev) => ({ ...prev, postalCode: true }));
                    }}
                    aria-invalid={showError('postalCode') || undefined}
                    disabled={isViewMode}
                  />
                )}
              />
              {showError('postalCode') && <p className="text-xs text-destructive">{errors.postalCode?.message}</p>}
            </div>
            <div className="flex-1 min-w-0 space-y-1.5 w-full">
              <Label>Country</Label>
              <Input value="ZA" disabled />
            </div>
          </div>
        </div>
      </MainCard>

      <MainCard className="mb-4">
        <h3 className="text-lg font-semibold mb-4">Primary Contact Person</h3>
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-4 items-start">
            <div className="flex-1 min-w-0 space-y-1.5 w-full">
              <Label htmlFor="firstName">First Name <span className="text-destructive">*</span></Label>
              <Input
                id="firstName"
                placeholder="e.g. John"
                {...registerWithTouched('firstName')}
                aria-invalid={showError('firstName') || undefined}
                disabled={isViewMode}
              />
              {showError('firstName') && <p className="text-xs text-destructive">{errors.firstName?.message}</p>}
            </div>
            <div className="flex-1 min-w-0 space-y-1.5 w-full">
              <Label htmlFor="lastName">Last Name <span className="text-destructive">*</span></Label>
              <Input
                id="lastName"
                placeholder="e.g. Smith"
                {...registerWithTouched('lastName')}
                aria-invalid={showError('lastName') || undefined}
                disabled={isViewMode}
              />
              {showError('lastName') && <p className="text-xs text-destructive">{errors.lastName?.message}</p>}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="emailAddress">Email Address <span className="text-destructive">*</span></Label>
            <Input
              id="emailAddress"
              type="email"
              placeholder="e.g. name@example.co.za"
              {...registerWithTouched('emailAddress')}
              aria-invalid={showError('emailAddress') || undefined}
              disabled={isViewMode}
            />
            {showError('emailAddress') && <p className="text-xs text-destructive">{errors.emailAddress?.message}</p>}
          </div>
          <div className="space-y-1.5">
            <Label className={cn(showError('phoneFullDigits') && 'text-destructive')}>
              Phone Number <span className="text-destructive">*</span>
            </Label>
            <div
              className={cn(
                'flex h-8 w-full rounded-lg border overflow-hidden bg-transparent',
                showError('phoneFullDigits') ? 'border-destructive' : 'border-input',
                'focus-within:border-ring focus-within:ring-3 focus-within:ring-ring/50',
              )}
            >
              {(() => {
                const phoneBuffer = (watch('phoneFullDigits') ?? '').replace(/\D/g, '');
                const { countryDigits, nationalDigits } = splitBuffer(phoneBuffer);
                const matchedOpt = COUNTRY_PHONE_OPTIONS.find((o) => o.dialDigits === countryDigits);
                return (
                  <>
                    <select
                      value={matchedOpt ? matchedOpt.dial : ''}
                      onChange={(e) => {
                        const dial = e.target.value;
                        const opt = COUNTRY_PHONE_OPTIONS.find((o) => o.dial === dial);
                        if (opt) setValue('phoneFullDigits', opt.dialDigits + nationalDigits);
                        setTouched((p) => ({ ...p, phoneFullDigits: true }));
                        trigger('phoneFullDigits');
                      }}
                      disabled={isViewMode}
                      className="h-full min-w-[120px] bg-muted px-2 text-sm border-0 outline-none appearance-none cursor-pointer"
                    >
                      <option value="">Country code</option>
                      {COUNTRY_PHONE_OPTIONS.map((opt) => (
                        <option key={opt.code} value={opt.dial}>
                          {opt.flag} {opt.dial} {opt.label}
                        </option>
                      ))}
                    </select>
                    <div className="w-px bg-border self-stretch" />
                    <Controller
                      name="phoneFullDigits"
                      control={control}
                      render={({ field }) => {
                        const buf = (field.value ?? '').replace(/\D/g, '');
                        const { countryDigits: cd, nationalDigits: nd } = splitBuffer(buf);
                        return (
                          <input
                            placeholder="e.g. 27 64 082 2549 or 123-456-7890"
                            maxLength={PHONE_FULL_INPUT_MAX_LENGTH + 4}
                            inputMode="tel"
                            className="flex-1 min-w-0 h-full px-2.5 text-sm bg-transparent border-0 outline-none placeholder:text-muted-foreground"
                            value={formatPhoneWithDashes(nd)}
                            onChange={(e) => {
                              const raw = e.target.value;
                              setValue('phoneContainsInvalidChars', /[A-Za-z<>]/.test(raw));
                              const newNational = raw.replace(/\D/g, '').slice(0, PHONE_NATIONAL_MAX_LENGTH);
                              const newBuffer = cd + newNational;
                              field.onChange(newBuffer);
                              setTouched((p) => ({ ...p, phoneFullDigits: true }));
                              trigger('phoneFullDigits');
                            }}
                            onKeyDown={(e) => {
                              if (e.key !== 'Backspace') return;
                              if (nd !== '') return;
                              if (buf.length === 0) return;
                              e.preventDefault();
                              field.onChange(buf.slice(0, -1));
                              setTouched((p) => ({ ...p, phoneFullDigits: true }));
                              trigger('phoneFullDigits');
                            }}
                            onBlur={() => {
                              field.onBlur();
                              setTouched((p) => ({ ...p, phoneFullDigits: true }));
                              trigger('phoneFullDigits');
                            }}
                            disabled={isViewMode}
                          />
                        );
                      }}
                    />
                  </>
                );
              })()}
            </div>
            {showError('phoneFullDigits') && (
              <p className="text-xs text-destructive">
                {errors.phoneFullDigits?.message ? String(errors.phoneFullDigits.message) : ''}
              </p>
            )}
          </div>
        </div>
      </MainCard>

      {isCompleteMode && (
        <div className="flex justify-end gap-2 mt-4">
          <Button type="button" variant="outline" onClick={() => navigate('/spar-mobile/store-compliance')}>Cancel</Button>
          <Button
            type="submit"
            disabled={submitting || !hasValidatedOnce || !isValid}
            onClick={() => setSubmitAttempted(true)}
          >
            {submitting ? 'Submitting…' : 'Submit Details'}
          </Button>
        </div>
      )}
      </form>
    </div>
  );
}
